import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, RefreshControl, Pressable, TextInput } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Redirect } from 'expo-router';

import { api } from '../../src/api/client';
import { useAdminPermissions } from '../../src/hooks/useAdminPermissions';
import ContentContainer from '../../src/components/ContentContainer';
import { useTheme } from '../../src/context/ThemeContext';
import { useResponsive } from '../../src/utils/responsive';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';

const MOBILE_BREAKPOINT = 768;
const CENTS = 100;

type Property = 'all' | 'sales' | 'app' | 'mobile';
type Metric = 'pageviews' | 'visitors' | 'conversions';

interface Totals { pageviews: number; visitors: number; sessions: number; events: number; conversions: number }
interface SourceRow { source: string; visitors: number; sessions: number; leads: number; spendCents: number; costPerLeadCents: number | null }
interface Summary {
  range: { days: number; since: string };
  property: Property;
  totals: Totals;
  previous: Totals;
  topSources: SourceRow[];
  topPages: { path: string; pageviews: number }[];
  topReferrers: { referrer: string; count: number }[];
  devices: { device: string; count: number }[];
  byProperty: { property: string; pageviews: number; visitors: number; conversions: number }[];
}
interface SeriesPoint { day: string; pageviews: number; visitors: number; conversions: number }
interface SpendRow { id: string; month: string; source: string; amountCents: number; note: string | null }

const PROPERTIES: { value: Property; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'sales', label: 'Sales funnel' },
  { value: 'app', label: 'App (www)' },
  { value: 'mobile', label: 'Mobile (m)' },
];
const RANGES: { value: number; label: string }[] = [
  { value: 7, label: '7d' }, { value: 30, label: '30d' }, { value: 90, label: '90d' },
];
const METRICS: { value: Metric; label: string }[] = [
  { value: 'pageviews', label: 'Pageviews' }, { value: 'visitors', label: 'Visitors' }, { value: 'conversions', label: 'Conversions' },
];
const PROP_LABEL: Record<string, string> = { sales: 'Sales funnel', app: 'App (www)', mobile: 'Mobile (m)' };

const money = (cents: number) => `$${(cents / CENTS).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
const num = (n: number) => n.toLocaleString('en-US');
const currentMonth = () => new Date().toISOString().slice(0, 7);
function delta(cur: number, prev: number): { pct: number; up: boolean } | null {
  if (prev <= 0) return cur > 0 ? { pct: 100, up: true } : null;
  const pct = Math.round(((cur - prev) / prev) * 100);
  return { pct: Math.abs(pct), up: pct >= 0 };
}

export default function AdminWebAnalytics() {
  const { has, loaded } = useAdminPermissions();
  if (loaded && !has('platform_web_analytics')) return <Redirect href="/admin/users" />;
  const canWrite = has('platform_web_analytics', 'write');
  const { colors } = useTheme();
  const { width } = useResponsive();
  const isMobile = width < MOBILE_BREAKPOINT;
  const styles = useStyles(colors, isMobile);

  const [property, setProperty] = useState<Property>('all');
  const [days, setDays] = useState(30);
  const [metric, setMetric] = useState<Metric>('pageviews');

  const [summary, setSummary] = useState<Summary | null>(null);
  const [series, setSeries] = useState<SeriesPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [spend, setSpend] = useState<SpendRow[]>([]);
  const [showSpend, setShowSpend] = useState(false);
  const [fSource, setFSource] = useState('');
  const [fAmount, setFAmount] = useState('');
  const [fMonth, setFMonth] = useState(currentMonth());
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async (prop: Property, d: number, signal?: AbortSignal) => {
    try {
      const qs = new URLSearchParams({ property: prop, days: String(d) }).toString();
      const [s, t] = await Promise.all([
        api<Summary>(`/api/admin/web-analytics/summary?${qs}`),
        api<{ series: SeriesPoint[] }>(`/api/admin/web-analytics/timeseries?${qs}`),
      ]);
      if (signal?.aborted) return;
      setSummary(s);
      setSeries(t.series);
    } catch (err) { if (!signal?.aborted) console.error(err); }
    finally { if (!signal?.aborted) { setLoading(false); setRefreshing(false); } }
  }, []);

  const fetchSpend = useCallback(async (signal?: AbortSignal) => {
    try {
      const d = await api<{ spend: SpendRow[] }>('/api/admin/web-analytics/spend');
      if (!signal?.aborted) setSpend(d.spend);
    } catch (err) { if (!signal?.aborted) console.error(err); }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    fetchData(property, days, ac.signal);
    return () => ac.abort();
  }, [property, days, fetchData]);

  useEffect(() => {
    const ac = new AbortController();
    fetchSpend(ac.signal);
    return () => ac.abort();
  }, [fetchSpend]);

  const onRefresh = () => { setRefreshing(true); fetchData(property, days); fetchSpend(); };

  const saveSpend = async () => {
    const amountCents = Math.round(parseFloat(fAmount) * CENTS);
    if (!fSource.trim() || !Number.isFinite(amountCents) || amountCents < 0 || !/^\d{4}-\d{2}$/.test(fMonth)) return;
    setSaving(true);
    try {
      await api('/api/admin/web-analytics/spend', { method: 'POST', body: { month: fMonth, source: fSource.trim(), amountCents } });
      setFSource(''); setFAmount('');
      await Promise.all([fetchSpend(), fetchData(property, days)]);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const deleteSpend = async (id: string) => {
    try {
      await api(`/api/admin/web-analytics/spend/${id}`, { method: 'DELETE' });
      await Promise.all([fetchSpend(), fetchData(property, days)]);
    } catch (err) { console.error(err); }
  };

  if (loading) return <View style={styles.center}><ActivityIndicator size="large" color={colors.primary} /></View>;
  if (!summary) return <View style={styles.center}><Text style={styles.empty}>Failed to load web analytics</Text></View>;

  const t = summary.totals, p = summary.previous;
  const convRate = t.sessions > 0 ? ((t.conversions / t.sessions) * 100).toFixed(1) : '0.0';
  const chartMax = Math.max(1, ...series.map(s => s[metric]));

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />}
    >
      <ContentContainer role="admin">

        <View style={styles.banner}>
          <Ionicons name="information-circle-outline" size={16} color={colors.textSecondary} />
          <Text style={styles.bannerText}>First-party, cookieless traffic for sales / www / m. Visitors are counted by a daily anonymized hash — no cookies, no PII.</Text>
        </View>

        {/* Filters */}
        <View style={styles.filterRow}>
          <Segmented options={PROPERTIES.map(o => ({ value: o.value, label: o.label }))} value={property} onChange={(v) => setProperty(v as Property)} styles={styles} colors={colors} />
          <Segmented options={RANGES.map(o => ({ value: String(o.value), label: o.label }))} value={String(days)} onChange={(v) => setDays(Number(v))} styles={styles} colors={colors} />
        </View>

        {/* KPI cards */}
        <View style={styles.kpiGrid}>
          <Kpi label="Visitors" value={num(t.visitors)} d={delta(t.visitors, p.visitors)} styles={styles} colors={colors} />
          <Kpi label="Pageviews" value={num(t.pageviews)} d={delta(t.pageviews, p.pageviews)} styles={styles} colors={colors} />
          <Kpi label="Sessions" value={num(t.sessions)} d={delta(t.sessions, p.sessions)} styles={styles} colors={colors} />
          <Kpi label="Conversions" value={num(t.conversions)} sub={`${convRate}% of sessions`} d={delta(t.conversions, p.conversions)} styles={styles} colors={colors} />
        </View>

        {/* Trend chart */}
        <View style={styles.card}>
          <View style={styles.chartHead}>
            <Text style={styles.cardTitle}>Daily trend</Text>
            <Segmented options={METRICS.map(o => ({ value: o.value, label: o.label }))} value={metric} onChange={(v) => setMetric(v as Metric)} styles={styles} colors={colors} small />
          </View>
          <View style={styles.chart}>
            {series.map((s, i) => {
              const h = Math.round((s[metric] / chartMax) * 120);
              return (
                <View key={s.day} style={styles.barWrap}>
                  <View style={[styles.bar, { height: Math.max(2, h), backgroundColor: colors.primary }]} />
                  {(i === 0 || i === series.length - 1 || i === Math.floor(series.length / 2)) && (
                    <Text style={styles.barLabel} numberOfLines={1}>{s.day.slice(5)}</Text>
                  )}
                </View>
              );
            })}
          </View>
          <Text style={styles.chartFoot}>{metric} · last {days} days · peak {num(chartMax)}</Text>
        </View>

        {/* Top sources + cost per lead */}
        <Text style={styles.sectionTitle}>Top sources</Text>
        <View style={styles.card}>
          <View style={styles.tHead}>
            <Text style={[styles.cSource, styles.h]}>Source</Text>
            <Text style={[styles.cNum, styles.h]}>Visitors</Text>
            <Text style={[styles.cNum, styles.h]}>Leads</Text>
            {!isMobile && <Text style={[styles.cNum, styles.h]}>Spend</Text>}
            <Text style={[styles.cNum, styles.h]}>Cost/Lead</Text>
          </View>
          {summary.topSources.length === 0 ? (
            <Text style={styles.empty}>No traffic yet</Text>
          ) : summary.topSources.map((s, i) => (
            <View key={s.source + i} style={[styles.tRow, i === summary.topSources.length - 1 && styles.lastRow]}>
              <Text style={[styles.cSource, styles.cell]} numberOfLines={1}>{s.source}</Text>
              <Text style={[styles.cNum, styles.cell, styles.mono]}>{num(s.visitors)}</Text>
              <Text style={[styles.cNum, styles.cell, styles.mono]}>{num(s.leads)}</Text>
              {!isMobile && <Text style={[styles.cNum, styles.cell, styles.mono]}>{s.spendCents > 0 ? money(s.spendCents) : '—'}</Text>}
              <Text style={[styles.cNum, styles.cell, styles.mono, s.costPerLeadCents != null && styles.cellPrimary]}>{s.costPerLeadCents != null ? money(s.costPerLeadCents) : '—'}</Text>
            </View>
          ))}
        </View>

        {/* Property split */}
        <Text style={styles.sectionTitle}>By property</Text>
        <View style={styles.card}>
          {summary.byProperty.length === 0 ? <Text style={styles.empty}>No data</Text> : summary.byProperty.map((b, i) => (
            <View key={b.property} style={[styles.lineItem, i === summary.byProperty.length - 1 && styles.lineItemLast]}>
              <Text style={styles.lineLabel}>{PROP_LABEL[b.property] || b.property}</Text>
              <Text style={styles.lineValue}>{num(b.visitors)} visitors · {num(b.pageviews)} views · {num(b.conversions)} conv</Text>
            </View>
          ))}
        </View>

        {/* Pages / referrers / devices */}
        <View style={isMobile ? undefined : styles.twoCol}>
          <MiniList title="Top pages" rows={summary.topPages.map(x => ({ label: x.path, value: num(x.pageviews) }))} styles={styles} flex />
          <MiniList title="Top referrers" rows={summary.topReferrers.map(x => ({ label: x.referrer, value: num(x.count) }))} styles={styles} flex />
        </View>
        <MiniList title="Devices (visitors)" rows={summary.devices.map(x => ({ label: x.device, value: num(x.count) }))} styles={styles} />

        {/* Ad spend manager */}
        <Pressable style={styles.spendToggle} onPress={() => setShowSpend(v => !v)}>
          <Ionicons name="cash-outline" size={16} color={colors.text} />
          <Text style={styles.spendToggleText}>Manual ad spend (cost-per-lead)</Text>
          <Ionicons name={showSpend ? 'chevron-up' : 'chevron-down'} size={16} color={colors.textSecondary} />
        </Pressable>
        {showSpend && (
          <View style={styles.card}>
            {canWrite && (
              <View style={styles.spendForm}>
                <TextInput style={[styles.input, styles.inSource]} placeholder="source (e.g. google-ads)" placeholderTextColor={colors.textLight} value={fSource} onChangeText={setFSource} autoCapitalize="none" />
                <TextInput style={[styles.input, styles.inMonth]} placeholder="YYYY-MM" placeholderTextColor={colors.textLight} value={fMonth} onChangeText={setFMonth} autoCapitalize="none" />
                <TextInput style={[styles.input, styles.inAmt]} placeholder="$ amount" placeholderTextColor={colors.textLight} value={fAmount} onChangeText={setFAmount} keyboardType="decimal-pad" />
                <Pressable style={[styles.saveBtn, saving && styles.saveBtnDisabled]} onPress={saveSpend} disabled={saving}>
                  <Text style={styles.saveBtnText}>{saving ? '…' : 'Save'}</Text>
                </Pressable>
              </View>
            )}
            {spend.length === 0 ? (
              <Text style={styles.empty}>No spend entered. Add a row to compute cost-per-lead.</Text>
            ) : spend.map((row) => (
              <View key={row.id} style={styles.spendRow}>
                <Text style={styles.spendCell}>{row.month}</Text>
                <Text style={[styles.spendCell, styles.spendSource]} numberOfLines={1}>{row.source}</Text>
                <Text style={[styles.spendCell, styles.mono]}>{money(row.amountCents)}</Text>
                {canWrite && (
                  <Pressable onPress={() => deleteSpend(row.id)} hitSlop={8}>
                    <Ionicons name="trash-outline" size={16} color="#dc2626" />
                  </Pressable>
                )}
              </View>
            ))}
          </View>
        )}

      </ContentContainer>
    </ScrollView>
  );
}

function Segmented({ options, value, onChange, styles, colors, small }: {
  options: { value: string; label: string }[]; value: string; onChange: (v: string) => void;
  styles: ReturnType<typeof createStyles>; colors: ColorTheme; small?: boolean;
}) {
  return (
    <View style={styles.segmented}>
      {options.map(o => {
        const active = o.value === value;
        return (
          <Pressable key={o.value} onPress={() => onChange(o.value)} style={[styles.segItem, small && styles.segItemSmall, active && { backgroundColor: colors.primary }]}>
            <Text style={[styles.segText, small && styles.segTextSmall, active && styles.segTextActive]}>{o.label}</Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function Kpi({ label, value, sub, d, styles, colors }: {
  label: string; value: string; sub?: string; d: { pct: number; up: boolean } | null;
  styles: ReturnType<typeof createStyles>; colors: ColorTheme;
}) {
  return (
    <View style={styles.kpi}>
      <Text style={styles.kpiLabel}>{label}</Text>
      <Text style={styles.kpiValue}>{value}</Text>
      <View style={styles.kpiFoot}>
        {d && (
          <Text style={[styles.kpiDelta, { color: d.up ? '#059669' : '#dc2626' }]}>
            {d.up ? '▲' : '▼'} {d.pct}%
          </Text>
        )}
        {sub && <Text style={styles.kpiSub}>{sub}</Text>}
      </View>
    </View>
  );
}

function MiniList({ title, rows, styles, flex }: {
  title: string; rows: { label: string; value: string }[]; styles: ReturnType<typeof createStyles>; flex?: boolean;
}) {
  return (
    <View style={[styles.card, flex && styles.flex1]}>
      <Text style={styles.cardTitle}>{title}</Text>
      {rows.length === 0 ? <Text style={styles.empty}>No data</Text> : rows.map((r, i) => (
        <View key={r.label + i} style={[styles.lineItem, i === rows.length - 1 && styles.lineItemLast]}>
          <Text style={styles.lineLabel} numberOfLines={1}>{r.label}</Text>
          <Text style={[styles.lineValue, styles.mono]}>{r.value}</Text>
        </View>
      ))}
    </View>
  );
}

const createStyles = (c: ColorTheme, isMobile: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: isMobile ? spacing.md : spacing.lg },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.sm, marginTop: spacing.md },
  empty: { textAlign: 'center', color: c.textSecondary, paddingVertical: spacing.md, fontSize: fontSize.sm },
  flex1: { flex: 1 },
  mono: { fontVariant: ['tabular-nums'] },

  banner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    backgroundColor: c.surface, borderRadius: borderRadius.md,
    borderLeftWidth: 3, borderLeftColor: c.primary, marginBottom: spacing.md,
  },
  bannerText: { fontSize: fontSize.xs, color: c.textSecondary, flex: 1 },

  filterRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.md, justifyContent: 'space-between' },
  segmented: { flexDirection: 'row', backgroundColor: c.surface, borderRadius: borderRadius.md, padding: 3, flexWrap: 'wrap' },
  segItem: { paddingHorizontal: spacing.md, paddingVertical: 7, borderRadius: borderRadius.sm },
  segItemSmall: { paddingHorizontal: spacing.sm, paddingVertical: 5 },
  segText: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.medium },
  segTextSmall: { fontSize: fontSize.xs },
  segTextActive: { color: '#fff', fontWeight: fontWeight.semibold },

  kpiGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.md },
  kpi: {
    backgroundColor: c.surface, borderRadius: borderRadius.lg, padding: spacing.md,
    flexGrow: 1, flexBasis: isMobile ? '45%' : '22%', minWidth: isMobile ? 140 : 160,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1,
  },
  kpiLabel: { fontSize: fontSize.xs, color: c.textSecondary, textTransform: 'uppercase', fontWeight: fontWeight.semibold },
  kpiValue: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.text, marginTop: 4, fontVariant: ['tabular-nums'] },
  kpiFoot: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2, flexWrap: 'wrap' },
  kpiDelta: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold },
  kpiSub: { fontSize: fontSize.xs, color: c.textLight },

  card: {
    backgroundColor: c.surface, borderRadius: borderRadius.lg, padding: spacing.md, marginBottom: spacing.md,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 1,
  },
  cardTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.sm },
  twoCol: { flexDirection: 'row', gap: spacing.md },

  chartHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm, flexWrap: 'wrap', gap: spacing.sm },
  chart: { flexDirection: 'row', alignItems: 'flex-end', height: 140, gap: 1 },
  barWrap: { flex: 1, alignItems: 'center', justifyContent: 'flex-end', height: 140 },
  bar: { width: '80%', borderTopLeftRadius: 2, borderTopRightRadius: 2, minWidth: 2 },
  barLabel: { fontSize: 9, color: c.textLight, marginTop: 3 },
  chartFoot: { fontSize: fontSize.xs, color: c.textLight, marginTop: spacing.sm, textTransform: 'capitalize' },

  tHead: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  h: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, textTransform: 'uppercase' },
  tRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  lastRow: { borderBottomWidth: 0 },
  cell: { fontSize: fontSize.sm, color: c.text },
  cellPrimary: { color: c.primary, fontWeight: fontWeight.semibold },
  cSource: { flex: 1.6 },
  cNum: { flex: 1, textAlign: 'right', paddingLeft: spacing.sm },

  lineItem: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: spacing.sm },
  lineItemLast: { borderBottomWidth: 0 },
  lineLabel: { fontSize: fontSize.sm, color: c.text, flex: 1 },
  lineValue: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.medium },

  spendToggle: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: spacing.sm, marginTop: spacing.xs },
  spendToggleText: { flex: 1, fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  spendForm: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.sm, alignItems: 'center' },
  input: { backgroundColor: c.background, borderWidth: 1, borderColor: c.borderLight, borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, paddingVertical: 8, color: c.text, fontSize: fontSize.sm },
  inSource: { flex: 2, minWidth: 140 },
  inMonth: { width: 100 },
  inAmt: { width: 100 },
  saveBtn: { backgroundColor: c.primary, borderRadius: borderRadius.sm, paddingHorizontal: spacing.md, paddingVertical: 9 },
  saveBtnDisabled: { opacity: 0.6 },
  saveBtnText: { color: '#fff', fontSize: fontSize.sm, fontWeight: fontWeight.semibold },
  spendRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: spacing.sm },
  spendCell: { fontSize: fontSize.sm, color: c.text },
  spendSource: { flex: 1 },
});

const useStyles = (c: ColorTheme, isMobile: boolean) => React.useMemo(() => createStyles(c, isMobile), [c, isMobile]);
